/**
 * Data-loading hook: users, organisations, licenses, activation eligibility.
 *
 * Extracted from UserManagementPage.tsx (Sub-PR 6.3).
 */

import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  listUsers,
  listOrganisations,
  listLicenses,
  getActivationStatus,
  type ActivationEligibilityResponse,
} from '../../services/admin'
import type { License } from '../../types/admin'
import type { User } from '../../types'
import type { UserManagementOrganisation } from './types'
import { logger } from '../../utils/logger'

interface UseUserDataResult {
  users: User[]
  organisations: UserManagementOrganisation[]
  licenses: License[]
  loading: boolean
  error: string | null
  setError: (msg: string | null) => void
  activationStatus: Map<string, ActivationEligibilityResponse>
  loadData: () => Promise<void>
}

export function useUserData(
  isPlatformAdmin: boolean,
  searchQuery: string = ''
): UseUserDataResult {
  const { t } = useTranslation('admin')
  const [users, setUsers] = useState<User[]>([])
  const [organisations, setOrganisations] = useState<UserManagementOrganisation[]>([])
  const [licenses, setLicenses] = useState<License[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activationStatus, setActivationStatus] = useState<
    Map<string, ActivationEligibilityResponse>
  >(new Map())

  const loadActivationStatuses = useCallback(async (allUsers: User[]) => {
    const nonActiveUsers = allUsers.filter((usr) => usr.status !== 'active')
    const statusMap = new Map<string, ActivationEligibilityResponse>()
    await Promise.all(
      nonActiveUsers.map(async (usr) => {
        try {
          const status = await getActivationStatus(usr.id)
          statusMap.set(usr.id, status)
        } catch {
          // Ignore individual errors
        }
      })
    )
    setActivationStatus(statusMap)
  }, [])

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      // FR3: pass search to backend listUsers; empty string is treated as no
      // filter by the backend handler (and elided from the query string by
      // services/admin.ts buildQueryString). The page is expected to debounce
      // searchQuery before passing it here -- see useDebouncedValue.
      // exactOptionalPropertyTypes: true requires we omit `search` rather
      // than setting it to undefined.
      const trimmedSearch = searchQuery.trim()
      const userListParams = trimmedSearch
        ? { limit: 100, search: trimmedSearch }
        : { limit: 100 }
      const [usersRes, orgsRes, licensesRes] = await Promise.all([
        listUsers(userListParams),
        isPlatformAdmin ? listOrganisations({ limit: 100 }) : Promise.resolve({ data: [] }),
        isPlatformAdmin ? listLicenses({ limit: 100 }) : Promise.resolve({ data: [] }),
      ])
      setUsers(usersRes.data)
      setOrganisations(orgsRes.data)
      setLicenses(licensesRes.data)
      await loadActivationStatuses(usersRes.data)
    } catch (err) {
      setError(t('users.errors.loadFailed'))
      logger.error('useUserData', 'Failed to load user management data', err)
    } finally {
      setLoading(false)
    }
  }, [t, isPlatformAdmin, loadActivationStatuses, searchQuery])

  useEffect(() => {
    void loadData()
  }, [loadData])

  return { users, organisations, licenses, loading, error, setError, activationStatus, loadData }
}
